if (!customElements.get("product-coupon")) {
  customElements.define(
    "product-coupon",
    class ProductCoupon extends HTMLElement {
      constructor() {
        super();
        this.appliedCode = "";
        this.discountMeta = null;
        this.busy = false;
      }

      connectedCallback() {
        this.input = this.querySelector(".product-coupon__input");
        this.button = this.querySelector(".product-coupon__submit");
        this.status = this.querySelector(".product-coupon__status");
        this.variants = this.parseVariants();

        this.onSubmit = this.onSubmit.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onInputCapitalize = this.onInputCapitalize.bind(this);
        this.onQuantityChange = this.onQuantityChange.bind(this);
        this.button.addEventListener("click", this.onSubmit);
        this.input.addEventListener("keydown", this.onKeydown);
        this.input.addEventListener("input", this.onInputCapitalize);
        document.addEventListener("change", this.onQuantityChange, true);
        document.addEventListener("input", this.onQuantityChange, true);

        this.syncFromCart();
      }

      disconnectedCallback() {
        this.button?.removeEventListener("click", this.onSubmit);
        this.input?.removeEventListener("keydown", this.onKeydown);
        this.input?.removeEventListener("input", this.onInputCapitalize);
        document.removeEventListener("change", this.onQuantityChange, true);
        document.removeEventListener("input", this.onQuantityChange, true);
        clearTimeout(this.qtyTimer);
      }

      parseVariants() {
        const script = this.querySelector("[data-product-coupon-variants]");
        if (!script) return [];
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return [];
        }
      }

      onKeydown(event) {
        if (event.key === "Enter") {
          event.preventDefault();
          this.onSubmit();
        }
      }

      onInputCapitalize() {
        const input = this.input;
        if (!input || input.readOnly) return;
        const start = input.selectionStart;
        const end = input.selectionEnd;
        const upper = input.value.toUpperCase();
        if (input.value === upper) return;
        input.value = upper;
        if (start != null && end != null) {
          input.setSelectionRange(start, end);
        }
      }

      async onSubmit() {
        if (this.busy) return;
        if (this.appliedCode) {
          await this.removeCoupon();
          return;
        }
        await this.applyCoupon();
      }

      getVariantId() {
        const input = document.querySelector(
          `#product-form-${this.dataset.section} input[name="id"]`
        );
        const value = Number(input?.value || this.dataset.variantId);
        return Number.isFinite(value) ? value : null;
      }

      getCurrentVariant() {
        const id = this.getVariantId();
        return (
          this.variants.find((variant) => Number(variant.id) === id) || {
            id,
            price: 0,
            compare_at_price: null,
          }
        );
      }

      getQuantityInput() {
        const sectionId = this.dataset.section;
        return (
          document.querySelector(`#Quantity-${sectionId}`) ||
          document.querySelector(
            `#product-form-${sectionId} input[name="quantity"]`
          ) ||
          document.querySelector(
            ".product__info-container input.quantity__input"
          )
        );
      }

      getQuantity() {
        const qty = parseInt(this.getQuantityInput()?.value, 10);
        return Number.isFinite(qty) && qty > 0 ? qty : 1;
      }

      isProductQuantityControl(target) {
        if (!target || !target.closest) return false;
        if (target.closest("cart-items, cart-drawer-items, .cart-item")) {
          return false;
        }
        return Boolean(
          target.closest("quantity-input") ||
            target.matches?.('input[name="quantity"], .quantity__input') ||
            target.closest(".product__info-container .quantity")
        );
      }

      onQuantityChange(event) {
        if (!this.appliedCode || this.busy) return;
        if (!this.isProductQuantityControl(event.target)) return;
        clearTimeout(this.qtyTimer);
        this.qtyTimer = setTimeout(() => {
          this.syncFromVariant();
        }, 300);
      }

      setBusy(isBusy) {
        this.busy = isBusy;
        this.classList.toggle("is-busy", isBusy);
        this.button.disabled = isBusy;
        this.input.disabled = isBusy && !this.appliedCode;
      }

      setStatus(message, type) {
        if (!this.status) return;
        const hasMessage = Boolean(message);
        this.status.hidden = !hasMessage;
        this.status.textContent = message || "";
        this.status.classList.toggle("is-success", type === "success");
        this.status.classList.toggle("is-error", type === "error");
      }

      setAppliedState(code) {
        this.appliedCode = code;
        this.input.value = code;
        this.input.readOnly = true;
        this.button.textContent = this.applyLabel("remove");
        this.setStatus(this.applyLabel("applied", { code }), "success");
      }

      clearAppliedState() {
        this.appliedCode = "";
        this.discountMeta = null;
        this.input.readOnly = false;
        this.button.textContent = this.applyLabel("apply");
      }

      applyLabel(key, vars) {
        const labels = window.productCouponStrings || {};
        let text = labels[key] || key;
        if (vars) {
          Object.keys(vars).forEach((name) => {
            text = text.replace(`{{ ${name} }}`, vars[name]);
          });
        }
        return text;
      }

      async applyCoupon() {
        const code = (this.input.value || "").trim().toUpperCase();
        if (!code) {
          this.setStatus(this.applyLabel("empty"), "error");
          this.input.focus();
          return;
        }

        this.setBusy(true);
        this.setStatus("");
        try {
          const result = await this.evaluateCode(code);
          if (!result.ok) {
            this.setStatus(result.message || this.applyLabel("invalid"), "error");
            return;
          }
          this.discountMeta = result.meta;
          this.setAppliedState(code);
          this.paintPrice();
          this.syncEmiPrice();
        } catch (error) {
          console.error(error);
          this.setStatus(this.applyLabel("error"), "error");
        } finally {
          this.setBusy(false);
        }
      }

      async removeCoupon() {
        this.setBusy(true);
        try {
          await this.updateCart({ discount: "" });
          this.clearAppliedState();
          this.setStatus("");
          this.restorePrice();
          this.syncEmiPrice(true);
        } catch (error) {
          console.error(error);
          this.setStatus(this.applyLabel("error"), "error");
        } finally {
          this.setBusy(false);
        }
      }

      async syncFromCart() {
        try {
          const cart = await this.getCart();
          const stored = (cart.discount_codes || []).find((entry) => entry.code);
          if (!stored?.code) return;
          const result = await this.evaluateCode(stored.code, { persist: false });
          if (!result.ok) return;
          this.discountMeta = result.meta;
          this.setAppliedState(stored.code);
          this.paintPrice();
          this.syncEmiPrice();
        } catch (error) {
          console.error(error);
        }
      }

      async syncFromVariant() {
        if (!this.appliedCode) return;
        try {
          const result = await this.evaluateCode(this.appliedCode, {
            persist: false,
          });
          if (!result.ok) return;
          this.discountMeta = result.meta;
          this.paintPrice();
          this.syncEmiPrice();
        } catch (error) {
          console.error(error);
        }
      }

      async evaluateCode(code, { persist = true } = {}) {
        const variantId = this.getVariantId();
        if (!variantId) {
          return { ok: false, message: this.applyLabel("error") };
        }

        const preview = await this.previewOnStorefront(
          variantId,
          code,
          this.getQuantity()
        );
        if (!preview.ok) return preview;

        if (persist) {
          await this.updateCart({ discount: code });
        }

        return preview;
      }

      async previewOnStorefront(variantId, code, quantity = 1) {
        const shop = this.dataset.shop || window.Shopify?.shop;
        if (!shop) {
          return { ok: false, message: this.applyLabel("error") };
        }

        const qty = Number(quantity) > 0 ? Number(quantity) : 1;
        const response = await fetch(
          `https://${shop}/api/2025-01/graphql.json`,
          {
            method: "POST",
            credentials: "omit",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              query: `mutation cartCreate($input: CartInput!) {
                cartCreate(input: $input) {
                  cart {
                    discountCodes { code applicable }
                    lines(first: 1) {
                      nodes {
                        quantity
                        cost {
                          amountPerQuantity { amount }
                          subtotalAmount { amount }
                          totalAmount { amount }
                        }
                        discountAllocations {
                          discountedAmount { amount }
                        }
                      }
                    }
                  }
                  userErrors { message }
                }
              }`,
              variables: {
                input: {
                  lines: [
                    {
                      merchandiseId: `gid://shopify/ProductVariant/${variantId}`,
                      quantity: qty,
                    },
                  ],
                  discountCodes: [code],
                },
              },
            }),
          }
        );

        const payload = await response.json();
        const payloadErrors = payload.errors || [];
        const mutation = payload.data?.cartCreate;
        const userError = mutation?.userErrors?.[0]?.message;
        if (!response.ok || payloadErrors.length || userError) {
          return {
            ok: false,
            message: userError || this.applyLabel("error"),
          };
        }

        const cart = mutation?.cart;
        const codeInfo = (cart?.discountCodes || []).find(
          (entry) => entry.code?.toUpperCase() === code.toUpperCase()
        );
        const line = cart?.lines?.nodes?.[0];
        const originalUnitCents = this.amountToCents(
          line?.cost?.amountPerQuantity?.amount
        );
        const lineQty = Number(line?.quantity) || qty;
        const subtotalCents = this.amountToCents(line?.cost?.subtotalAmount?.amount);
        const totalCents = this.amountToCents(line?.cost?.totalAmount?.amount);
        const discountedUnitCents = Math.round(totalCents / lineQty);
        const allocatedCents = (line?.discountAllocations || []).reduce(
          (sum, allocation) =>
            sum + this.amountToCents(allocation?.discountedAmount?.amount),
          0
        );

        if (!codeInfo?.applicable || !line) {
          return { ok: false, message: this.applyLabel("invalid") };
        }

        if (allocatedCents <= 0 && totalCents >= subtotalCents) {
          return { ok: false, message: this.applyLabel("invalid") };
        }

        return {
          ok: true,
          meta: {
            originalCents: originalUnitCents,
            discountedCents: Math.max(0, discountedUnitCents),
            allocatedCents,
            quantity: lineQty,
          },
        };
      }

      amountToCents(amount) {
        const value = Number.parseFloat(amount);
        if (!Number.isFinite(value)) return 0;
        return Math.round(value * 100);
      }

      computeDiscounted(priceCents) {
        if (this.discountMeta && this.discountMeta.discountedCents != null) {
          return Math.max(0, Number(this.discountMeta.discountedCents));
        }
        return Number(priceCents) || 0;
      }

      formatProductMoney(cents) {
        if (typeof formatMoney === "function") {
          return String(
            formatMoney(cents, (window.theme && window.theme.moneyFormat) || "")
          ).replace(/\.00(?!\d)/, "");
        }
        return `₹${Math.round(Number(cents || 0) / 100)}`;
      }

      formatPercent(value) {
        const rounded = Math.round(value * 10) / 10;
        return Number.isInteger(rounded)
          ? String(Math.round(value))
          : String(rounded);
      }

      paintPrice() {
        const root = document.getElementById(`price-${this.dataset.section}`);
        const priceEl = root?.querySelector(".price");
        if (!priceEl || !this.appliedCode) return;

        const variant = this.getCurrentVariant();
        const original = Number(variant.price) || 0;
        const compareAt = Number(variant.compare_at_price) || 0;
        const discounted = this.computeDiscounted(original);
        if (discounted >= original) return;

        const compare = Math.max(compareAt, original);
        priceEl.classList.add("price--on-sale");

        const salePrice = priceEl.querySelector(".price-item--sale");
        const comparePrice = priceEl.querySelector(
          ".price__compare .price-item--regular"
        );
        if (salePrice) salePrice.textContent = this.formatProductMoney(discounted);
        if (comparePrice) {
          comparePrice.textContent = this.formatProductMoney(compare);
        }

        const allocated = Number(this.discountMeta?.allocatedCents) || 0;
        const amounts = priceEl.querySelector(".price__sale .price__amounts");
        let discountEl = priceEl.querySelector(".price__discount");
        if (!discountEl && amounts) {
          discountEl = document.createElement("dd");
          discountEl.className = "price__discount";
          amounts.appendChild(discountEl);
        }
        if (discountEl) {
          if (allocated > 0) {
            discountEl.textContent = `${this.formatProductMoney(allocated)} off`;
          } else {
            const percent =
              compare > 0 ? ((compare - discounted) * 100) / compare : 0;
            discountEl.textContent = `${this.formatPercent(percent)}% off`;
          }
        }
      }

      restorePrice() {
        const root = document.getElementById(`price-${this.dataset.section}`);
        const priceEl = root?.querySelector(".price");
        if (!priceEl) return;

        const variant = this.getCurrentVariant();
        const original = Number(variant.price) || 0;
        const compareAt = Number(variant.compare_at_price) || 0;

        if (compareAt > original) {
          priceEl.classList.add("price--on-sale");
          const salePrice = priceEl.querySelector(".price-item--sale");
          const comparePrice = priceEl.querySelector(
            ".price__compare .price-item--regular"
          );
          if (salePrice) salePrice.textContent = this.formatProductMoney(original);
          if (comparePrice) {
            comparePrice.textContent = this.formatProductMoney(compareAt);
          }
          const percent = ((compareAt - original) * 100) / compareAt;
          const discountEl = priceEl.querySelector(".price__discount");
          if (discountEl) {
            discountEl.textContent = `${this.formatPercent(percent)}% off`;
          }
          return;
        }

        priceEl.classList.remove("price--on-sale");
      }

      syncEmiPrice(reset) {
        const wraps = document.querySelectorAll(
          '.emi-checkout-template-wrap[data-emi-location="product"], .emi-checkout-button-wrap[data-emi-location="product"]'
        );
        const variant = this.getCurrentVariant();
        const original = Number(variant.price) || 0;
        const unit = reset ? original : this.computeDiscounted(original);
        wraps.forEach((wrap) => {
          wrap.setAttribute("data-emi-unit-price", String(unit));
        });
        if (typeof window.refreshEmiCheckoutButtons === "function") {
          window.refreshEmiCheckoutButtons();
        }
      }

      async getCart() {
        const cartUrl = `${window.routes?.cart_url || "/cart"}.js`;
        const response = await fetch(cartUrl, {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });
        return response.json();
      }

      async updateCart(body) {
        return this.postCart(
          window.routes?.cart_update_url || "/cart/update.js",
          body
        );
      }

      async postCart(url, body) {
        const response = await fetch(url, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        });
        return response.json();
      }
    }
  );
}
